import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xing-com/button';
import type { SerpsRouteParams } from '@xing-com/crate-jobs-domain-serps-helpers';
import { Headline } from '@xing-com/typography';

import type { JobItemResultVisibleJob } from '../../../helpers';
import { ResultsList } from '../results-list';

import * as S from './serp-nearby-results.styles';
import { useSerpNearbyResults } from './use-serp-nearby-results';

const INITIAL_VISIBLE_RESULTS = 10;
const VISIBLE_RESULTS_STEP = 10;

type Props = {
  serps: SerpsRouteParams | null;
  mainResults: JobItemResultVisibleJob[];
  primaryJobCount: number;
};

export const SerpNearbyResults: React.FC<Props> = ({
  serps,
  mainResults,
  primaryJobCount,
}) => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_RESULTS);
  const {
    jobs,
    total,
    fetchedCount,
    isLoading,
    isFetchingMore,
    shouldFetch,
    fetchNextPage,
  } = useSerpNearbyResults({
    serps,
    mainResults,
    primaryJobCount,
  });

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_RESULTS);
  }, [serps, primaryJobCount]);

  if (!shouldFetch || isLoading || jobs.length === 0) {
    return null;
  }

  const visibleJobs = jobs.slice(0, visibleCount);
  const canShowMore = visibleCount < jobs.length || fetchedCount < total;

  const handleShowMore = async (): Promise<void> => {
    const nextVisibleCount = visibleCount + VISIBLE_RESULTS_STEP;

    if (jobs.length < nextVisibleCount && fetchedCount < total) {
      await fetchNextPage();
    }

    setVisibleCount(nextVisibleCount);
  };

  return (
    <S.Container>
      <S.Header>
        <Headline size="medium" sizeWide="large">
          <FormattedMessage id="JOBS_SEARCH_SERP_NEARBY_RESULTS_HEADLINE" />
        </Headline>
      </S.Header>
      <ResultsList items={visibleJobs} type="EMPTY_STATE_SEARCH" />
      {canShowMore ? (
        <S.Actions>
          <Button
            onClick={handleShowMore}
            variant="secondary"
            loading={isFetchingMore}
          >
            <FormattedMessage id="JOBS_SEARCH_PAGINATION_LOAD_MORE" />
          </Button>
        </S.Actions>
      ) : null}
    </S.Container>
  );
};
